import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import dynamic from 'next/dynamic'

import {
  ProductSort,
  FacetInput,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ReadMore } from '@thg-commerce/gravity-patterns'
import { useScrollLock } from '@thg-commerce/gravity-system'
import { SafeHtml } from '@thg-commerce/gravity-elements'
import { OptionsType } from '@thg-commerce/gravity-elements/CustomDropdown/CustomDropdown'
import {
  i18n,
  useSiteConfig,
  useFormattableI18nProperty,
  useHeaderHeight,
  useTheme,
} from '@thg-commerce/enterprise-core'

import {
  ProductCategories,
  ProductCategoryItemProps,
} from '@thg-commerce/enterprise-widget-product-categories'
import { ZIndexLevel } from '@thg-commerce/enterprise-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { ProductItem as ProductItemComponent } from './ProductItem'

import {
  VerticalFacetSkeleton,
  FacetType,
  HorizontalFacetSkeleton,
  HorizontalFacetsProps,
  VerticalFacetsProps,
} from './Facets'

import {
  Container,
  FacetProductWrapper,
  ProductListIntroGrid,
  ProductList,
  Title,
  HiddenTitle,
  StyledGridItem,
  RefineButton,
  RefineIconTextWrapper,
  RefineText,
  StyledRefineIcon,
  StyledPagination,
  TotalResults,
  StyledDropdown,
  AccessibilityButton,
  VerticalFacetsWrapper,
  HideOnMobile,
  ProductListItem,
  TotalResultsStyledGridItem,
  ProductListGrid,
  DescriptionStyledGridItem,
  ProductListFooterGrid,
  ProductListGridItem,
  StickyFilterGrid,
} from './styles'
import { facetsCallbacks } from './dataTracking'

export enum Focus {
  VERTICAL_FACETS_TITLE = 'VERTICAL_FACETS_TITLE',
  PRODUCT_LIST_TITLE = 'PRODUCT_LIST_TITLE',
}

export interface PageOptions {
  sort?: ProductSort
  facets?: FacetInput[]
  pageNumber?: number
}

export interface ProductListPageProps {
  title?: string
  description?: string
  seoDescription?: string
  categories?: ProductCategoryItemProps[]
  categoriesTitle?: string
  facets: FacetType[]
  products: any
  onInputChange: (newInput: PageOptions) => void
  pageOptions: Required<PageOptions>
  sortTypes: {
    type: ProductSort
    displayText: string
  }[]
  itemsPerPage: number
  totalItems: number
  loading?: boolean
  changeType?: ChangeType
}

export enum ChangeType {
  NONE,
  FACET,
  SORT,
  PAGE,
}

const MobileFacets = dynamic<any>(() =>
  import('./Facets/MobileFacets/MobileFacets').then((mod) => mod.MobileFacets),
)
const VerticalFacets = dynamic<VerticalFacetsProps>(() =>
  import('./Facets/VerticalFacets/VerticalFacets').then(
    (mod) => mod.VerticalFacets,
  ),
)
const HorizontalFacets = dynamic<HorizontalFacetsProps>(() =>
  import('./Facets/HorizontalFacets/HorizontalFacets').then(
    (mod) => mod.HorizontalFacets,
  ),
)

const ProductItem = withHydrationOnDemand({ on: ['visible'] })(
  ProductItemComponent,
)

export const ProductListPage = (props: ProductListPageProps) => {
  const theme = useTheme()
  const {
    hasDiscountSortOption,
    hideProductListTotalResults,
    hideProductListReadMore,
    hideProductListFooterReadMore,
    enableHorizontalFacets,
    hideProductListReviewRating,
    hideProductListProductBlockButton,
    productListReviewRatingThreshold,
    showMarketedSpecialOfferIcon,
    hideProductListImageRollover,
    showProductListProductBlockIcons,
    hasClickAndCollect,
    showPdpLinkWhenOutOfStock,
    previewQuickbuy,
    showProductBlockBrandTitle,
    hideProductListSwatch,
    enableStickyFacetsOnMobile,
  } = useSiteConfig()

  const [showMobileFacets, setShowMobileFacets] = React.useState(false)

  const [focusedElement, setFocusedElement] = React.useState<Focus | null>(null)

  const productListPageTitleRef = React.useRef<HTMLHeadingElement>(null)

  const verticalFacetsTitleRef = React.useRef<HTMLHeadingElement>(null)

  const setScrollLock = useScrollLock()

  const headerHeight = useHeaderHeight()

  const handleGoToProductSection = () => {
    setFocusedElement(Focus.PRODUCT_LIST_TITLE)
    productListPageTitleRef?.current?.focus()
  }

  const handleGoToRefineSection = () => {
    setFocusedElement(Focus.VERTICAL_FACETS_TITLE)
    verticalFacetsTitleRef?.current?.focus()
  }

  React.useEffect(() => {
    typeof document !== 'undefined' &&
      typeof window !== 'undefined' &&
      (showMobileFacets
        ? setScrollLock(true, document, window)
        : setScrollLock(false, document, window))
  }, [showMobileFacets])

  const i18nText = {
    readMore: i18n('general.readmore.text'),
    readLess: i18n('general.readless.text'),
    sortBy: i18n('product.list.sort.arialabel'),
    productsHiddenTitle: i18n('general.accessibility.products.label'),
    rrpText: i18n('product.price.rrp.text'),
    paginationi18nText: {
      next: i18n('general.pagination.next'),
      previous: i18n('general.pagination.previous.shorthand'),
      ofTotal: i18n('general.pagination.oftotal'),
      page: i18n('general.pagination.page'),
      dropdownLabel: i18n('general.pagination.dropdownlabel'),
      currentlyOnText: i18n('general.pagination.currentlyontext'),
      ariaLabel: i18n('general.pagination.arialabel'),
      totalResultsText: i18n('general.pagination.totalresults.text').replace(
        '%s',
        props.totalItems.toString(),
      ),
    },
  }

  const i18nFacetsText = {
    back: i18n('general.back.text'),
    clear: i18n('product.list.facets.clear.text'),
    save: i18n('product.list.facets.submit.text'),
    close: i18n('product.list.facets.close.text'),
    refine: i18n('product.list.facets.refine.text'),
    errorMessage: i18n('general.error.generic.text'),
    clearAll: i18n('product.list.facets.clearall.text'),
    editRefine: i18n('product.list.facets.editrefine.text'),
    sliderHeader: i18n('product.list.filter.slider.header'),
    filterText: i18n('product.list.facets.expandfilter.text'),
    searchBrandLabel: i18n('product.list.facets.searchbrand.label'),
    goToRefineSection: i18n('product.accessibility.go.to.refine.section'),
    minimumLabel: i18n('product.list.filter.price.minimum.price.ariaLabel'),
    maximumLabel: i18n('product.list.filter.price.maximum.price.ariaLabel'),
    goToProductSection: i18n('product.accessibility.go.to.product.section'),
    searchBrandPlaceholder: i18n('product.list.facets.searchbrand.placeholder'),
  }

  const productI18nText = {
    buyAriaLabel: useFormattableI18nProperty('general.productbuynow.label'),
    buyText: i18n('general.productbuynow.text'),
    quickBuyText: i18n('general.product.quickbuy.text'),
    soldOutText: i18n('general.productsoldout.text'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
    closeI18nText: {
      closeAriaLabel: i18n('general.modal.close.button.arialabel'),
      closeLabel: i18n('general.modal.close.button.label'),
    },
    freeGiftMessage: i18n('product.marketedspecialoffer.freegift.text'),
    swatchUnavailableText: i18n(
      'product.productoptions.swatch.tooltip.unavailable.text',
    ),
    swatchCloseButtonText: i18n(
      'product.productoptions.swatch.tooltip.close.text',
    ),
  }

  const wishlistI18nText = {
    addToWishlistText: i18n('product.addtowishlistbutton.addtowishlist.text'),
    savedToWishlistText: i18n(
      'product.addtowishlistbutton.savedtowishlist.text',
    ),
    wishlistTooltip: {
      closeButtonText: i18n('product.addtowishlist.tooltip.button.close.text'),
    },
    wishlistTooltipContent: {
      loginSignupText: i18n('product.addtowishlist.tooltip.loginsignup.text'),
      toUseWishlistText: i18n('product.addtowishlist.tooltip.text'),
    },
  }

  const PLP_IMAGES_EAGERLY_LOADED = 8

  const productBlocks = React.useMemo(() => {
    return props.products.map((product, index) => (
      <ProductListItem
        isHorizontalFacetsEnabled={enableHorizontalFacets}
        data-testid="product-item"
      >
        <ProductItem
          attributesInsetSpacing={
            theme.widget.productList.attributesInsetSpacing
          }
          style={{ width: '100%' }}
          key={index}
          quickBuyFeatureEnabled={previewQuickbuy}
          productI18nText={productI18nText}
          wishlistI18nText={wishlistI18nText}
          lazy={index > PLP_IMAGES_EAGERLY_LOADED - 1}
          siteConfig={{
            hideProductListReviewRating,
            hideProductListProductBlockButton,
            productListReviewRatingThreshold,
            showMarketedSpecialOfferIcon,
            hideProductListImageRollover,
            showProductListProductBlockIcons,
            hasClickAndCollect,
            showPdpLinkWhenOutOfStock,
            enableHorizontalFacets,
            showProductBlockBrandTitle,
            hideProductListSwatch,
          }}
          hasFocusOnTitle={true}
          {...{
            ...product,
            price: {
              ...product.price,
              price: product.price.price,
              rrp: product.price.rrp,
              rrpText: i18nText.rrpText,
            },
          }}
        />
      </ProductListItem>
    ))
  }, [props.products])

  const dropdownOptions: OptionsType[] = React.useMemo(
    () =>
      props.sortTypes
        .filter((option) =>
          option.type === ProductSort.DiscountPercentageHighToLow
            ? hasDiscountSortOption
            : true,
        )
        .map((option) => {
          return {
            key: option.type,
            value: option.displayText,
          }
        }),
    [props.sortTypes],
  )

  const selectedSort = props.sortTypes.find(
    (sort) => sort.type === props.pageOptions.sort,
  )?.type

  const showPagination = props.totalItems > props.itemsPerPage

  return (
    <Container
      id="ProductList"
      headerHeight={headerHeight}
      containerSpacing={theme.widget.productList.containerSpacing}
    >
      <FacetProductWrapper>
        {showMobileFacets && (
          <MobileFacets
            facets={props.facets}
            selectedFacets={props.pageOptions.facets}
            onInputChange={props.onInputChange}
            setShowMobileFacets={setShowMobileFacets}
            showMobileFacets={showMobileFacets}
            i18nText={i18nFacetsText}
          />
        )}
        {!enableHorizontalFacets && (
          <VerticalFacetsWrapper>
            {props.loading &&
            (props.changeType === ChangeType.FACET ||
              props.changeType === ChangeType.NONE) ? (
              <VerticalFacetSkeleton
                refineText={i18nFacetsText.refine}
                sections={Array(3).fill({
                  itemCount: 5,
                })}
              />
            ) : (
              <VerticalFacets
                facets={props.facets}
                selectedFacets={props.pageOptions.facets}
                onInputChange={props.onInputChange}
                i18nText={i18nFacetsText}
                onAccessibilityButtonClick={handleGoToProductSection}
                focusedElement={focusedElement}
                onAccessibilityButtonBlur={() => setFocusedElement(null)}
                verticalFacetsTitleRef={verticalFacetsTitleRef}
              />
            )}
          </VerticalFacetsWrapper>
        )}
        <div style={{ width: '100%' }}>
          <ProductListIntroGrid
            data-testid="products-intro-container"
            containerSpacing={theme.widget.productList.introSectionSpacing}
          >
            <StyledGridItem
              colSpan={[12, 10, 9, 8]}
              colStart={
                theme.widget.productList.header.horizontalAlignment ===
                HorizontalAlignment.CENTER
                  ? [1, 2, 2, 3]
                  : 1
              }
              marginTop={0}
            >
              {props.title && (
                <Title
                  data-testid="product-list-page-title"
                  tabIndex={
                    focusedElement === Focus.PRODUCT_LIST_TITLE ? 0 : -1
                  }
                  onBlur={() => setFocusedElement(null)}
                  ref={productListPageTitleRef}
                >
                  {props.title}
                </Title>
              )}
            </StyledGridItem>
            <TotalResultsStyledGridItem
              centerAlign
              colSpan={[12, 12, 3, 3]}
              colStart={[1, 1, 10, 10]}
            >
              {!hideProductListTotalResults && props.totalItems > 0 && (
                <TotalResults>
                  {i18nText.paginationi18nText.totalResultsText}
                </TotalResults>
              )}
            </TotalResultsStyledGridItem>
            {props.description && (
              <DescriptionStyledGridItem
                colSpan={
                  enableHorizontalFacets ? [12, 10, 8, 8] : [12, 10, 9, 8]
                }
                colStart={
                  theme.widget.productList.header.horizontalAlignment ===
                  HorizontalAlignment.CENTER
                    ? [1, 2, 2, 3]
                    : 1
                }
              >
                {!hideProductListReadMore ? (
                  <ReadMore
                    content={props.description}
                    readMoreText={i18nText.readMore}
                    readLessText={i18nText.readLess}
                    horizontalAlignment={
                      theme.widget.productList.header.horizontalAlignment
                    }
                    numberOfLinesToShow={
                      theme.widget.productList.readMoreSection
                        .numberOfLinesToShow
                    }
                  />
                ) : (
                  <SafeHtml content={props.description} />
                )}
              </DescriptionStyledGridItem>
            )}
            {props.categories && props.categories.length > 0 && (
              <StyledGridItem
                colSpan={
                  theme.widget.productList.header.horizontalAlignment ===
                  HorizontalAlignment.CENTER
                    ? [12, 12, 10, 10]
                    : 12
                }
                colStart={
                  theme.widget.productList.header.horizontalAlignment ===
                  HorizontalAlignment.CENTER
                    ? [1, 1, 2, 2]
                    : 1
                }
                removePadding
                marginBottom={
                  theme.widget.productList.header.marginBottomSpacing
                }
              >
                <ProductCategories
                  backgroundColour={theme.colors.palette.greys.white}
                  categories={props.categories}
                  title={props.categoriesTitle || undefined}
                  alignment={
                    theme.widget.productList.header.horizontalAlignment
                  }
                />
              </StyledGridItem>
            )}
          </ProductListIntroGrid>
          <StickyFilterGrid
            sticky={enableStickyFacetsOnMobile}
            containerSpacing={theme.widget.productList.introSectionSpacing}
          >
            <StyledGridItem
              colSpan={[6, 6, 3, 3]}
              colStart={1}
              marginTop={
                !props.categories && !enableStickyFacetsOnMobile ? 4 : undefined
              }
              isHorizontalFacetsEnabled={enableHorizontalFacets}
            >
              <StyledDropdown
                stickyPosition
                zIndex={ZIndexLevel.Base5}
                placeholder={i18nText.sortBy}
                aria-label={i18nText.sortBy}
                data-testid="sort-by-dropdown"
                options={dropdownOptions}
                useDefaultDropdownWidth
                width="100%"
                iconOverride={theme.widget.productList.sortBy.chevronIcon}
                onChange={(event) =>
                  props.onInputChange({
                    sort: props.sortTypes.find(
                      (sort) => sort.type === event.key,
                    )?.type,
                  })
                }
                selected={selectedSort}
              />
            </StyledGridItem>
            <StyledGridItem
              colSpan={[12, 12, 4, 4]}
              marginTop={
                !props.categories && !enableStickyFacetsOnMobile ? 4 : undefined
              }
              hideOnMobile
              isHorizontalFacetsEnabled={enableHorizontalFacets}
            >
              <AccessibilityButton
                data-testid="go-to-refine-section-button"
                emphasis="medium"
                sizing="regular"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleGoToRefineSection()
                  }
                }}
                onClick={handleGoToRefineSection}
              >
                {i18nFacetsText.goToRefineSection}
              </AccessibilityButton>
            </StyledGridItem>
            <StyledGridItem
              colSpan={[6, 6, 5, 4]}
              colStart={enableHorizontalFacets ? [7, 7, 9, 9] : [7, 7, 8, 9]}
              marginTop={
                !props.categories && !enableStickyFacetsOnMobile ? 4 : undefined
              }
              hasReverseColumnLayout={enableHorizontalFacets}
            >
              {
                <RefineButton
                  data-testid="mobile-facets-open-button"
                  onClick={React.useCallback(() => {
                    setShowMobileFacets(true)
                    facetsCallbacks.openMobileFacetsClicked()
                  }, [facetsCallbacks])}
                  emphasis={theme.widget.productList.filterButton.emphasis}
                  {...(theme.widget.productList.filterButton.border
                    ? {
                        style: {
                          borderWidth:
                            theme.widget.productList.filterButton.border.width,
                          borderStyle:
                            theme.widget.productList.filterButton.border.style,
                          borderRadius:
                            theme.widget.productList.filterButton.border.radius,
                          borderColor:
                            theme.widget.productList.filterButton.border.color,
                        },
                      }
                    : {})}
                >
                  <RefineIconTextWrapper>
                    <RefineText>
                      {props.pageOptions.facets.length > 0
                        ? i18nFacetsText.editRefine
                        : i18nFacetsText.refine}
                    </RefineText>
                    <StyledRefineIcon />
                  </RefineIconTextWrapper>
                </RefineButton>
              }
              {showPagination && (
                <HideOnMobile>
                  <StyledPagination
                    useCustomDropdown
                    totalItems={props.totalItems}
                    itemsPerPage={props.itemsPerPage}
                    activePage={props.pageOptions.pageNumber}
                    onPageChange={(page: number) => {
                      props.onInputChange({ pageNumber: page })
                    }}
                    i18nText={{ ...i18nText.paginationi18nText }}
                    trailingSlash={false}
                  />
                </HideOnMobile>
              )}
            </StyledGridItem>
            {enableHorizontalFacets && (
              <StyledGridItem colSpan={12} hideOnMobile>
                {props.loading &&
                (props.changeType === ChangeType.FACET ||
                  props.changeType === ChangeType.NONE) ? (
                  <HorizontalFacetSkeleton />
                ) : (
                  <HorizontalFacets
                    sortTypes={props.sortTypes}
                    dropDownOptions={dropdownOptions}
                    facets={props.facets}
                    selectedFacets={props.pageOptions.facets}
                    onInputChange={props.onInputChange}
                    i18nText={{ ...i18nFacetsText, sortBy: i18nText.sortBy }}
                    selectedSort={selectedSort}
                  />
                )}
              </StyledGridItem>
            )}
          </StickyFilterGrid>
          <ProductListGrid>
            <ProductListGridItem
              colSpan={12}
              marginTop={3}
              containerSpacing={theme.widget.productList.containerSpacing}
            >
              <HiddenTitle>{i18nText.productsHiddenTitle}</HiddenTitle>
              <ProductList data-testid="product-list-page-product-list">
                {props.products && productBlocks}
              </ProductList>
            </ProductListGridItem>
            {showPagination && (
              <StyledGridItem colSpan={[12, 12, 5, 4]} colStart={[1, 1, 8, 9]}>
                <StyledPagination
                  useCustomDropdown
                  totalItems={props.totalItems}
                  itemsPerPage={props.itemsPerPage}
                  activePage={props.pageOptions.pageNumber}
                  onPageChange={(page: number) => {
                    props.onInputChange({ pageNumber: page })
                    handleGoToProductSection()
                  }}
                  i18nText={{ ...i18nText.paginationi18nText }}
                  trailingSlash={false}
                  removeBlurFocus
                />
              </StyledGridItem>
            )}
            {props.seoDescription && (
              <ProductListFooterGrid
                colSpan={[12, 12, 12, 12]}
                containerSpacing={theme.widget.productList.readMoreSpacing}
              >
                {!hideProductListFooterReadMore ? (
                  <ReadMore
                    content={props.seoDescription}
                    readMoreText={i18nText.readMore}
                    readLessText={i18nText.readLess}
                    numberOfLinesToShow={
                      theme.widget.productList.readMoreSection
                        .numberOfLinesToShow
                    }
                  />
                ) : (
                  <SafeHtml content={props.seoDescription} />
                )}
              </ProductListFooterGrid>
            )}
          </ProductListGrid>
        </div>
      </FacetProductWrapper>
    </Container>
  )
}
